import { EmailAuthenticationResults, InboundEmail } from '../api/models';
import { StatusTag, TagTone } from './ui';

const classificationLabels: Record<string, string> = {
  raa_verification: 'RAA verification',
  cor_notice: 'Change of registrant',
  fast_transfer_auth: 'Fast transfer authorization',
  fast_transfer_status: 'Fast transfer status',
  renewal_lifecycle: 'Renewal lifecycle',
  contact_reminder: 'Contact reminder',
  account_security: 'Account security',
  login_code: 'Login code',
  outbound_transfer: 'Outbound transfer',
  order_receipt: 'Order receipt',
  sale_lead_payout: 'Sale, lead or payout',
  legal_abuse: 'Legal or abuse',
  direct_buyer: 'Direct buyer',
  marketplace_verification: 'Marketplace verification',
  marketplace_transaction: 'Marketplace transaction',
  marketing_spam: 'Marketing or spam',
  unknown: 'Unclassified',
};

const dispositionLabels: Record<string, string> = {
  pending_review: 'Pending review',
};

/** Ordered so the authentication checks always read the same way. */
const authCheckLabels: [keyof EmailAuthenticationResults, string][] = [
  ['dkimAligned', 'DKIM aligned'],
  ['spfAligned', 'SPF aligned'],
  ['senderAllowlisted', 'Sender allowlisted'],
  ['templateMatched', 'Template matched'],
  ['jobMatched', 'Correlated job'],
  ['linkAllowlisted', 'Link allowlisted'],
  ['tokenUnused', 'Token unused'],
];

export function humanizeToken(value: string): string {
  return value.replace(/_/g, ' ');
}

export function classificationLabel(classification: string): string {
  return classificationLabels[classification] ?? humanizeToken(classification);
}

export function dispositionLabel(disposition: string): string {
  return dispositionLabels[disposition] ?? humanizeToken(disposition);
}

/**
 * A low-confidence classification is the one a person is most likely to want to
 * second-guess, so it carries the warning tone.
 */
function confidenceTone(confidence?: string | null): TagTone {
  return confidence === 'low' ? 'warn' : 'accent';
}

interface ClassificationChipProps {
  email: Pick<InboundEmail, 'classification' | 'classificationConfidence'>;
}

export function ClassificationChip({ email }: ClassificationChipProps) {
  if (!email.classification) return null;
  return (
    <StatusTag tone={confidenceTone(email.classificationConfidence)} dot={false}>
      {classificationLabel(email.classification)}
      {email.classificationConfidence && (
        <span className="opacity-60 font-normal">{email.classificationConfidence}</span>
      )}
    </StatusTag>
  );
}

type RoutedIdentityFields = Pick<InboundEmail, 'routedAccount' | 'routedNcAccount' | 'routedDomain'>;

/**
 * Mail to a per-domain parser address resolves to the exact NC account and
 * domain, which is the registrar context a reviewer needs; the Afternic seller
 * is only shown, labelled, when that is all the recipient could resolve.
 */
export function routedIdentity(email: RoutedIdentityFields): string | null {
  if (email.routedNcAccount) {
    return email.routedDomain
      ? `${email.routedDomain} · NC ${email.routedNcAccount}`
      : `NC ${email.routedNcAccount}`;
  }
  if (email.routedAccount) return `seller ${email.routedAccount}`;
  return null;
}

interface RoutingCellProps {
  email: Pick<InboundEmail, 'classification' | 'classificationConfidence'> & RoutedIdentityFields;
}

/** List-cell summary of what the router decided about a message. */
export function RoutingCell({ email }: RoutingCellProps) {
  const identity = routedIdentity(email);
  if (!email.classification && !identity) {
    return <span className="text-text-3">—</span>;
  }
  return (
    <div className="flex flex-col gap-1 items-start">
      <ClassificationChip email={email} />
      {identity && <span className="text-text-3 text-[11px] font-mono">{identity}</span>}
    </div>
  );
}

interface RoutingDetailsProps {
  email: InboundEmail;
}

/**
 * The router's verdict on a message: how it was classified, whether it is
 * waiting on a person, and which authentication checks it failed.
 */
export function EmailRoutingDetails({ email }: RoutingDetailsProps) {
  const riskFlags = email.riskFlags ?? [];
  const auth = email.authenticationResults;
  const checks = auth ? authCheckLabels.filter(([key]) => auth[key] !== undefined) : [];

  if (!email.classification && !email.humanDisposition && riskFlags.length === 0 && checks.length === 0) {
    return null;
  }

  return (
    <div className="px-6 py-2.5 border-b border-border bg-bg flex flex-col gap-2 text-[12px]">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">Routing</span>
        <ClassificationChip email={email} />
        {routedIdentity(email) && (
          <span className="text-text-2 font-mono text-[11.5px]">{routedIdentity(email)}</span>
        )}
        {email.humanDisposition && (
          <StatusTag tone="warn" dot={false}>
            {dispositionLabel(email.humanDisposition)}
          </StatusTag>
        )}
      </div>

      {riskFlags.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mt-0.5">Risk flags</span>
          <div className="flex gap-1 flex-wrap">
            {riskFlags.map((flag) => (
              <StatusTag key={flag} tone="danger" dot={false}>
                {flag}
              </StatusTag>
            ))}
          </div>
        </div>
      )}

      {checks.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mt-0.5">
            Authentication
          </span>
          <div className="flex gap-x-3 gap-y-1 flex-wrap">
            {checks.map(([key, label]) => (
              <span
                key={key}
                className={`text-[11.5px] ${auth?.[key] ? 'text-success' : 'text-danger font-semibold'}`}
              >
                {auth?.[key] ? '✓' : '✕'} {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
